import type { Article as SearchProxyArticle } from '@wix/ambassador-answers-search-proxy/types';
import {
  ArticleSortType,
  Category,
  Label,
  Translation,
} from '@wix/answers-api';
import { Box } from '@wix/design-system';
import classNames from 'classnames';
import React, { FunctionComponent, useMemo } from 'react';

import {
  ArticleSearchResult,
  PageSeo,
  PageType,
  RoadmapFeatureResolution,
  RoadmapFilter,
} from '../../types';
import { useStore } from '../../zustand/stores';
import { AgentToolbar } from '../AgentToolbar';
import { PageView } from '../PageView';
import { SEO } from '../SEO';
import { UserEngagement } from '../UserEngagement';

import css from './index.module.scss';

export type PageDataArticle = SearchProxyArticle & {
  withRelatedArticles?: boolean;
  isFromV2?: boolean;
};

export type PageData = {
  article?: PageDataArticle;
  category?: Category;
  translations?: Translation[];
  roadmapData?: RoadmapPageData;
  searchResults?: ArticleSearchResult;
  term?: string;
};

export type RoadmapPageData = {
  sortType?: ArticleSortType;
  roadmapFilter?: RoadmapFilter;
  resolution?: RoadmapFeatureResolution;
  roadmapLabels?: Label[];
  label?: { name: string; id: string };
};

type PageTemplateProps = {
  children: React.ReactNode;
  pageType: PageType;
  pageSeo: PageSeo;
};

export const PageTemplate: FunctionComponent<PageTemplateProps> = ({
  children,
  pageType,
  pageSeo,
}) => {
  const { itemId, itemType, searchResults, term } = useStore(
    (state) => state.pageSlice
  );
  const { article, translations: articleTranslations } = useStore(
    (state) => state.articleSlice
  );
  const { category, translations: categoryTranslations } = useStore(
    (state) => state.categorySlice
  );
  const roadmapSlice = useStore((state) => state.roadmapSlice);

  const pageData: PageData = useMemo(
    () => ({
      article,
      category,
      translations: articleTranslations || categoryTranslations,
      roadmapData: roadmapSlice.sortType || roadmapSlice.roadmapFilter
        ? {
            sortType: roadmapSlice.sortType,
            roadmapFilter: roadmapSlice.roadmapFilter,
            resolution: roadmapSlice.resolution,
            roadmapLabels: roadmapSlice.roadmapLabels,
            label: roadmapSlice.label,
          }
        : undefined,
      searchResults,
      term,
    }),
    [
      article,
      category,
      articleTranslations,
      categoryTranslations,
      roadmapSlice,
      searchResults,
      term,
    ]
  );

  return (
    <Box
      direction="vertical"
      className={classNames('page-template', css.wrapper)}
    >
      <SEO pageData={pageData} pageType={pageType} pageSeo={pageSeo} />
      <AgentToolbar pageData={pageData} pageType={pageType} />
      <UserEngagement
        pageData={pageData}
        pageType={pageType}
        itemType={itemType as string}
        itemId={itemId}
      />
      <PageView pageData={pageData} pageType={pageType} itemId={itemId} />
      <main>{children}</main>
    </Box>
  );
};
